import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { eventsApi } from '@/api/events'
import type { Event } from '@/types/event'
import { Badge } from '@/components/ui/Badge'
import { TicketTypeBadge } from '@/components/events/TicketTypeBadge'
import { Spinner } from '@/components/ui/Spinner'
import { toast } from '@/store/toastStore'
import { formatFCFA, formatDate } from '@/lib/utils'

export function EventListPage() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState<number | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    eventsApi.list()
      .then((r) => setEvents(r.data))
      .catch(() => toast.error('Impossible de charger les événements.'))
      .finally(() => setLoading(false))
  }, [])

  const handlePublish = async (event: Event) => {
    if (event.status === 'published') return
    setPublishing(event.id)
    try {
      await eventsApi.publish(event.id)
      setEvents((prev) => prev.map((e) => e.id === event.id ? { ...e, status: 'published' } : e))
      toast.success('Événement publié avec succès.')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erreur lors de la publication.'
      toast.error(msg)
    } finally {
      setPublishing(null)
    }
  }

  const copyLink = (event: Event) => {
    navigator.clipboard.writeText(event.public_url)
    toast.success('Lien copié dans le presse-papiers.')
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Événements</h1>
          <p className="text-sm text-gray-500 mt-0.5">{events.length} événement{events.length !== 1 ? 's' : ''}</p>
        </div>
        <Link
          to="/events/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nouvel événement
        </Link>
      </div>

      {/* Empty state */}
      {events.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <p className="text-sm text-gray-500">Aucun événement pour l'instant.</p>
          <Link to="/events/new" className="mt-3 inline-flex text-sm text-primary-600 hover:underline font-medium">
            Créer le premier événement
          </Link>
        </div>
      )}

      {/* Cards */}
      <div className="flex flex-col gap-4">
        {events.map((event) => (
          <div key={event.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge label={event.status} variant={event.status} />
                  <h2 className="font-semibold text-gray-900 truncate">{event.name}</h2>
                </div>
                <p className="text-sm text-gray-500">
                  {formatDate(event.event_date)} · {event.event_time.slice(0, 5)} · {event.location}
                </p>

                {/* Récap tickets */}
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-sm font-mono text-gray-700">
                    {event.tickets_sold_total} vendus — {formatFCFA(event.revenue_total)}
                  </span>
                </div>

                {/* Badges types */}
                {event.ticket_types.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {event.ticket_types.map((t, i) => (
                      <TicketTypeBadge key={t.id} name={t.name} sortOrder={i} />
                    ))}
                  </div>
                )}
              </div>

              {/* Poster */}
              {event.poster_url && (
                <img
                  src={event.poster_url}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover shrink-0 hidden sm:block"
                />
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => navigate(`/events/${event.id}`)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Voir
              </button>
              {event.status === 'published' && (
                <button
                  onClick={() => copyLink(event)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                  Copier le lien
                </button>
              )}
              {event.status === 'draft' && (
                <button
                  onClick={() => handlePublish(event)}
                  disabled={publishing === event.id}
                  className="px-3 py-1.5 rounded-lg bg-success-500 hover:bg-success-600 text-white text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {publishing === event.id
                    ? <Spinner className="w-3.5 h-3.5 text-white" />
                    : <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                  }
                  Publier
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
